import { Box, CircularProgress, Typography } from '@mui/material';
import { IconButton, Tooltip } from '@mui/material';
import { navigate, type PageProps } from 'gatsby';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import { DrilldownHeader } from '@components/DrilldownHeader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import Icon from '@components/Icon/Icon';

import { DISCERN_JOBS } from '@constants/routes';

import { useAppLayout } from '@hooks/useAppLayout';

import AppLayout from '@layouts/AppLayout';
import { MainContainer } from '@layouts/DrilldownStyle';

import DiscernJobDocumentDrilldown from '@views/Discern/DiscernJobDocumentDrilldown';

import { useGetDiscernDocument } from 'generated/discern.generated';
import { useGetS3AssetRecordQuery } from 'generated/file.generated';
import { CurationStatus } from 'generated/types';

const POLL_INTERVAL_MS = 5000;

const messages = defineMessages({
  title: {
    id: 'discern.jobDrilldown.title',
    defaultMessage: 'Discern Job',
  },
  viewPdf: {
    id: 'discern.jobDrilldown.viewPdf',
    defaultMessage: 'View PDF',
  },
  downloadPdf: {
    id: 'discern.jobDrilldown.downloadPdf',
    defaultMessage: 'Download PDF',
  },
  checkingStatus: {
    id: 'discern.jobDrilldown.checkingStatus',
    defaultMessage: 'Checking for job updates…',
  },
});

function Content({ documentId }: { documentId: string }) {
  const { formatMessage } = useIntl();
  const { setTitle } = useAppLayout();
  const {
    data,
    loading: documentLoading,
    startPolling,
    stopPolling,
  } = useGetDiscernDocument({
    variables: { input: { discern_document_id: documentId } },
    skip: !documentId,
  });
  const document = data?.getDiscernDocument;
  const documentStatus = document?.status;
  const isPolling = React.useRef(false);

  React.useEffect(() => {
    if (!documentStatus) return;
    if (documentStatus !== CurationStatus.Structuring) {
      stopPolling();
      isPolling.current = false;
      return;
    }
    if (!isPolling.current) {
      startPolling(POLL_INTERVAL_MS);
      isPolling.current = true;
    }
    return () => {
      stopPolling();
      isPolling.current = false;
    };
  }, [documentStatus, startPolling, stopPolling]);

  const isActivelyPolling = documentStatus === CurationStatus.Structuring;

  const patientId = document?.vitall_patient?.patient_id;
  const binaryId = document?.binary_resource_id;
  const filename =
    document?.grouping_state_json.filename || `${documentId}.pdf`;

  const { data: s3AssetData, loading: s3AssetLoading } =
    useGetS3AssetRecordQuery({
      variables: {
        input: {
          patient_id: patientId ?? '',
          record_id: binaryId ?? '',
        },
      },
      skip: !binaryId || !patientId,
      fetchPolicy: 'network-only',
    });

  const pdfUrl = s3AssetData?.getS3AssetRecord.presignedGetUrl;

  // Only show the full loading skeleton on the initial fetch; keep content
  // visible during subsequent poll cycles when data already exists.
  const loading =
    (documentLoading && !data) || (s3AssetLoading && !s3AssetData);

  const title = formatMessage(messages.title);

  React.useEffect(() => {
    setTitle(filename);
  }, [filename, setTitle]);

  const headerItems = document?.status
    ? [
        {
          title: document.status,
          iconName: 'circle' as const,
          iconColor:
            document.status === CurationStatus.Failed
              ? 'error.main'
              : 'primary.main',
        },
      ]
    : [];

  const buttonsElement = (
    <>
      <Tooltip title={formatMessage(messages.viewPdf)}>
        <span>
          <IconButton
            disabled={!pdfUrl}
            component="a"
            href={pdfUrl || '#'}
            target="_blank"
            rel="noreferrer"
            sx={{ color: 'primary.contrastText' }}
          >
            <Icon name="eye" type="fas" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={formatMessage(messages.downloadPdf)}>
        <span>
          <IconButton
            disabled={!pdfUrl}
            component="a"
            href={pdfUrl || '#'}
            target="_blank"
            rel="noreferrer"
            sx={{ color: 'primary.contrastText' }}
          >
            <Icon name="download" type="fas" />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );

  return (
    <>
      <DrilldownHeader
        title={filename || title}
        items={headerItems}
        buttonsElement={buttonsElement}
        onBackClick={() => navigate(DISCERN_JOBS)}
      />
      <MainContainer>
        {isActivelyPolling && (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            px={2}
            py={1}
            data-testid="polling-indicator"
          >
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              {formatMessage(messages.checkingStatus)}
            </Typography>
          </Box>
        )}
        <ContentLoader loading={loading}>
          {document && <DiscernJobDocumentDrilldown document={document} />}
        </ContentLoader>
      </MainContainer>
    </>
  );
}
export default function DiscernJobDrilldownPage({ params }: PageProps) {
  const { formatMessage } = useIntl();
  const documentId = params.documentId as string;

  const title = formatMessage(messages.title);

  const WrappedContent = withErrorBoundary(Content, {
    FallbackComponent: ErrorBoundaryFallback,
  });

  return (
    <AppLayout friendlyName={title}>
      <WrappedContent documentId={documentId} />
    </AppLayout>
  );
}
